import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Eye, Edit2, Trash2, Pause, Download, MessageSquare } from 'lucide-react';
import SEO from '../components/SEO';
import TrustSubNav from '../components/TrustSubNav';

// =============================================================================
// GIWAHS — Trust Centre · Privacy page (review-only)
// Source of truth: /app/memory/founder_vault/39_GIWAHS_Privacy_Page.md
// =============================================================================

const RIGHTS = [
  { slug: 'access',      icon: Eye,          name: 'Access',          desc: 'See exactly what we hold about you.',                       mailto: 'subject=Data%20Access%20Request' },
  { slug: 'rectify',     icon: Edit2,        name: 'Rectify',         desc: 'Fix anything that is wrong.',                                mailto: 'subject=Data%20Rectification' },
  { slug: 'erase',       icon: Trash2,       name: 'Erase',           desc: 'Delete your account and data (subject to legal retention).', mailto: 'subject=Data%20Deletion%20Request' },
  { slug: 'restrict',    icon: Pause,        name: 'Restrict / object', desc: 'Limit what we do with your data.',                       mailto: 'subject=Data%20Processing%20Restriction' },
  { slug: 'portability', icon: Download,     name: 'Portability',     desc: 'Get your data in a machine-readable format.',                mailto: 'subject=Data%20Export%20Request' },
  { slug: 'complain',    icon: MessageSquare, name: 'Complain',       desc: 'Lodge a complaint with us, or escalate to a regulator.',     mailto: 'subject=Privacy%20Complaint' },
];

const TrustPrivacy = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Trust Centre', url: '/trust' },
    { name: 'Privacy', url: '/trust/privacy' },
  ];

  return (
    <div data-testid="trust-privacy-page" className="bg-white">
      <SEO
        title="Privacy — Trust Centre"
        description="GIWAHS privacy posture in plain English: no sale of data, no cross-context behavioural advertising, six rights (access · rectify · erase · restrict · portability · complain), GDPR / UK GDPR / CCPA / NZ Privacy Act 2020 / AU Privacy Act aligned."
        breadcrumbs={breadcrumbs}
      />

      {/* Hero */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <Link to="/trust" className="text-xs uppercase tracking-wider text-neutral-500 font-mono hover:text-neutral-900">
            ← Trust Centre
          </Link>
          <span className="mt-6 block label-overline">/ Privacy</span>
          <h1
            className="mt-4 max-w-4xl font-display tracking-tighter"
            style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.95 }}
          >
            Plain English.<br />
            Real rights.<br />
            <span className="text-neutral-500">No surprises.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">
            Your data belongs to you. We collect only what we need to run the platform, we never
            sell it, and every right you hold — access, correction, erasure, portability — works
            in practice, not just on paper. This page is the plain-English summary; the Privacy
            Policy in our document library is the binding contract. Both say the same thing.
          </p>
        </div>
      </section>

      <TrustSubNav />

      {/* 30-second summary */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 01 — The 30-second summary</span>
          <ul className="mt-6 grid max-w-4xl gap-4 md:grid-cols-2">
            {[
              "We don't sell your data. We don't share it for cross-context behavioural advertising.",
              "We collect what's necessary to run the Platform — account, billing metadata (no full card numbers), RFQ content, supplier verification documents, usage and device data.",
              'We share data only with Subprocessors listed at /trust/compliance, on documented data-protection terms.',
              'You can export, correct, or delete your data at any time — see "Your rights" below.',
            ].map((line) => (
              <li
                key={line}
                className="border border-neutral-900 bg-white p-5 text-sm leading-relaxed text-neutral-800"
              >
                {line}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Rights tiles */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 02 — Your rights</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            With verbs, not nouns.
          </h2>
          <div className="mt-10 grid gap-px bg-black md:grid-cols-3">
            {RIGHTS.map((r) => (
              <a
                key={r.slug}
                data-testid={`privacy-right-${r.slug}`}
                href={`mailto:privacy@giwahs.com?${r.mailto}`}
                className="group flex flex-col bg-white p-6 transition hover:bg-neutral-50"
              >
                <r.icon size={20} strokeWidth={1.5} />
                <div className="mt-5 font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>
                  {r.name}
                </div>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-600">{r.desc}</p>
                <div className="mt-4 inline-flex items-center gap-2 label-overline group-hover:gap-3 transition-all">
                  Email privacy@giwahs.com <ArrowUpRight size={14} strokeWidth={1.5} />
                </div>
              </a>
            ))}
          </div>
          <p className="mt-6 text-sm text-neutral-600">
            We respond within 30 days (or shorter, where the law requires) and may need to verify
            your identity first.
          </p>
        </div>
      </section>

      {/* What / Why / Basis */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 03 — What we collect, why, on what basis</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Mapped end-to-end.
          </h2>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse border border-black bg-white text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="border border-black p-4 text-left label-overline">What</th>
                  <th className="border border-black p-4 text-left label-overline">Why</th>
                  <th className="border border-black p-4 text-left label-overline">Legal basis</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Account info, organisation profile', 'Run your account', 'Contract'],
                  ['Billing metadata (Stripe holds full card numbers)', 'Process subscriptions, tax, invoices', 'Contract; legal obligation'],
                  ['RFQ content, supplier profiles, verification documents', 'Match buyers and suppliers; verify suppliers', 'Contract; legitimate interests'],
                  ['Usage & device data', 'Operate, secure, and improve the Platform', 'Legitimate interests'],
                  ['Transactional email metadata', 'Send receipts, renewal reminders, payment failures', 'Contract'],
                  ['Marketing email', 'Optional product updates', 'Consent (opt-in), withdrawable any time'],
                ].map((row) => (
                  <tr key={row[0]}>
                    <td className="border border-black p-4 font-medium">{row[0]}</td>
                    <td className="border border-black p-4 text-neutral-700">{row[1]}</td>
                    <td className="border border-black p-4 text-neutral-700">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Region-specific disclosures */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 04 — Region-specific disclosures</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Acknowledged in your jurisdiction.
          </h2>
          <div className="mt-10 grid gap-px bg-black md:grid-cols-2">
            {[
              { t: 'EU / UK / EEA / Switzerland', bullets: ['GDPR & UK GDPR compliant.', 'Legal bases documented per category.', 'Article 27 representative / DPO published at /legal/dpo when appointed.', 'Lodge complaints with your local DPA or the UK ICO.'] },
              { t: 'United States (CA · CO · VA · CT · UT · TX & others)', bullets: ['No sale or share of personal information.', 'No cross-context behavioural advertising.', 'Global Privacy Control (GPC) honoured.', 'Right to know · delete · correct · opt-out · limit use · non-discrimination.'] },
              { t: 'New Zealand', bullets: ['Privacy Act 2020 + Information Privacy Principles.', 'Complaints lodgeable with the Office of the NZ Privacy Commissioner (privacy.org.nz).', 'Notifiable breaches reported via NotifyUs as soon as practicable.'] },
              { t: 'Australia', bullets: ['Privacy Act 1988 + Australian Privacy Principles.', 'Complaints lodgeable with OAIC (oaic.gov.au).', 'Eligible data breaches under APP 11A cooperated with OAIC and affected individuals.'] },
            ].map((b) => (
              <div key={b.t} className="bg-white p-7">
                <div className="font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>{b.t}</div>
                <ul className="mt-4 space-y-2 text-sm leading-relaxed text-neutral-700">
                  {b.bullets.map((x) => <li key={x}>· {x}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Binding docs */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <span className="label-overline">/ 05 — Binding documents</span>
          <p className="mt-4 max-w-2xl text-sm text-neutral-700">
            We summarise here; the documents below are the binding versions. Each one opens
            directly from the Trust Centre document library.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm">
            {[
              { label: 'Privacy Policy',           slug: 'privacy',       t: 'privacy-binding-policy' },
              { label: 'Cookie Policy',            slug: 'cookies',       t: 'privacy-binding-cookies' },
              { label: 'Data Processing Addendum', slug: 'dpa',           t: 'privacy-binding-dpa' },
              { label: 'Subprocessor Register',    slug: 'subprocessors', t: 'privacy-binding-subprocessors' },
              { label: 'Security & IR Policy',     slug: 'security',      t: 'privacy-binding-security' },
            ].map((d) => (
              <a
                key={d.t}
                data-testid={d.t}
                href={`/legal/${d.slug}.html`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-neutral-900 bg-white px-3 py-2 text-xs uppercase tracking-wider font-mono text-neutral-700 hover:bg-neutral-50"
                title="Open document in a new tab"
              >
                {d.label}
                <ArrowUpRight size={12} strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-6 px-5 py-12 md:px-8">
          <div className="text-sm text-neutral-700">
            Privacy questions:{' '}
            <a data-testid="privacy-contact" href="mailto:privacy@giwahs.com" className="underline">
              privacy@giwahs.com
            </a>
          </div>
          <div className="text-sm text-neutral-600">
            See also: <Link to="/trust/security" className="underline">Security</Link> ·{' '}
            <Link to="/trust/compliance" className="underline">Compliance</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrustPrivacy;
